// src/components/HomeSection.jsx
import React, { useEffect, useState, useCallback } from "react";
import LightboxModal from "./LightBoxModal";
import { getLatestImages } from "../services/imageServices";
import { useNavigate } from "react-router-dom";

/** Inline placeholder for broken images (keeps layout stable) */
const BROKEN_IMG_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'>
       <rect width='100%' height='100%' fill='#eee'/>
       <text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='#888' font-family='sans-serif' font-size='16'>
         Image not found
       </text>
     </svg>`
  );

const PAINTING_SERVICES = [
  "Residential Painting",
  "Walls And Ceiling Painting",
  "Fence Painting",
  "Exterior Painting",
  "Interior Painting",
  "Roof Painting",
  "Commercial Painting",
  "Retail Shop Painting",
  "Factory Painting",
  "Office Painting",
  "School Painting",
  "Warehouse Painting",
  "Hotel Painting",
  "Garage Painting",
  "Industrial Painting",
];

const TILING_SERVICES = [
  "Residential Tiling",
  "Kitchen Tiling",
  "Bathroom Tiling",
  "Bathroom Renovation",
  "Caulking",
  "Grouting",
  "Waterproofing",
  "Balcony Tiling",
  "Commercial Tiling",
  "Pool Tiling",
  "Floor Tiling",
  "Wall Tiling",
  "Outdoor Tiling",
];

export default function HomeSection() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("whatWeDo");
  const [openHomeModal, setOpenHomeModal] = useState(null);

  // NEW: dropdown state for What We Do
  const [whatWeDoChoice, setWhatWeDoChoice] = useState("Painting");

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxInitial, setLightboxInitial] = useState(0);

  // Gallery data
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [hasFetchedImages, setHasFetchedImages] = useState(false);
  const [imagesError, setImagesError] = useState("");

  // Caption content (still used in description below grid if you want)
  const captionDescription =
    "See our gallery of completed works, from stunning bathrooms to vibrant living spaces and durable outdoor tiling.";

  const openHome = (section) => {
    setOpenHomeModal(section);
    document.body.style.overflow = "hidden";
  };
  const closeHome = () => {
    setOpenHomeModal(null);
    document.body.style.overflow = "";
  };

  const openLightbox = (imagesArray, startIndex = 0) => {
    setLightboxImages(Array.isArray(imagesArray) ? imagesArray : []);
    setLightboxInitial(Number.isFinite(startIndex) ? startIndex : 0);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "";
  };

  const goToGallery = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    closeHome();

    // Smooth scroll to the gallery
    requestAnimationFrame(() => {
      const el = document.getElementById("gallery");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate("#gallery", { replace: true });
    });
  };

  // Fetch images (used by both tab-activation prefetch & modal open)
  const fetchImages = useCallback(async () => {
    if (hasFetchedImages) return;
    setIsLoadingImages(true);
    setImagesError("");
    try {
      const urls = await getLatestImages();
      setGalleryImages(Array.isArray(urls) ? urls.slice(0, 4) : []);
      setHasFetchedImages(true);
      if (!urls || !urls.length) setImagesError("No images available.");
    } catch (err) {
      console.error("Failed to fetch latest images:", err);
      setImagesError("Failed to load images.");
      setGalleryImages([]);
    } finally {
      setIsLoadingImages(false);
    }
  }, [hasFetchedImages]);

  // If the modal opens, ensure images are loaded (for the “What We’ve Done” modal)
  useEffect(() => {
    if (openHomeModal === "whatWeveDone") {
      fetchImages();
    }
  }, [openHomeModal, fetchImages]);

  // Helpers to render images with graceful fallback
  const Thumb = ({ src, size = 96, radius = 10, onClick }) => (
    <img
      src={src}
      alt=""
      onClick={onClick}
      style={{
        width: size,
        height: size * 0.72,
        objectFit: "cover",
        borderRadius: radius,
        cursor: onClick ? "pointer" : "default",
        display: "block",
      }}
      onError={(ev) => {
        ev.currentTarget.onerror = null;
        ev.currentTarget.src = BROKEN_IMG_PLACEHOLDER;
        ev.currentTarget.style.objectFit = "contain";
        ev.currentTarget.title = "Image not found";
      }}
    />
  );

  const GridImg = ({ src, onClick }) => (
    <img
      src={src}
      alt=""
      onClick={onClick}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        borderRadius: "12px",
        cursor: onClick ? "zoom-in" : "default",
      }}
      onError={(ev) => {
        ev.currentTarget.onerror = null;
        ev.currentTarget.src = BROKEN_IMG_PLACEHOLDER;
        ev.currentTarget.style.objectFit = "contain";
        ev.currentTarget.title = "Image not found";
      }}
    />
  );

  // First four images for layout convenience
  const [img0, img1, img2, img3] = galleryImages;

  // Render list based on dropdown choice
  const renderServicesList = () => {
    const isPainting = whatWeDoChoice === "Painting";
    const items = isPainting ? PAINTING_SERVICES : TILING_SERVICES;
    return (
      <div style={{ marginTop: "1rem" }}>
        <h5 className="mb-2">{whatWeDoChoice}</h5>
        <ul style={{ columns: 2, columnGap: "2rem", paddingLeft: "1rem" }}>
          {items.map((item) => (
            <li key={item} style={{ breakInside: "avoid" }}>
              {item}
            </li>
          ))}
        </ul>
        <p style={{ marginTop: "1rem" }}>
          Contact us for a free quote or to discuss your project needs!
        </p>
      </div>
    );
  };

  return (
    <section id="home">
      {/* Hero */}
      <div
        className="hero"
        style={{
          backgroundImage: "url('/hero-image.jpg')",
        }}
      >
        <h1 className="hero__title">KH Tiling &amp; Painting Contractor</h1>
      </div>

      {/* Tabs */}
      <div className="home-tabs-container">
        {/* Tab: What We Do */}
        <div
          id="tabWhatWeDo"
          className={`home-tab ${activeTab === "whatWeDo" ? "active" : ""}`}
          tabIndex={0}
          role="button"
          aria-pressed={activeTab === "whatWeDo"}
          onClick={() => setActiveTab("whatWeDo")}
        >
          <h2>What We Do?</h2>
          <div className="home-tab-content">
            <p>
              We specialize in modern tiling and painting for residential and
              commercial spaces...
            </p>
            <button
              id="btnWhatWeDoDetails"
              className="home-tab-details-btn"
              onClick={(e) => {
                e.stopPropagation();
                setWhatWeDoChoice("Painting"); // default selection when opening
                openHome("whatWeDo");
              }}
            >
              See Details
            </button>
          </div>
        </div>

        {/* Tab: What We've Done (adds preview + lazy prefetch) */}
        <div
          id="tabWhatWeveDone"
          className={`home-tab ${activeTab === "whatWeveDone" ? "active" : ""}`}
          tabIndex={0}
          role="button"
          aria-pressed={activeTab === "whatWeveDone"}
          onClick={async () => {
            setActiveTab("whatWeveDone");
            fetchImages();
          }}
        >
          <h2>What We've Done</h2>

          <div className="home-tab-content">
            {/* PREVIEW ROW */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "auto auto 1fr",
                alignItems: "center",
                gap: "12px",
                marginBottom: "10px",
                minHeight: 80,
              }}
            >
              <div style={{ display: "flex", gap: "12px" }}>
                {img0 ? <Thumb src={img0} /> : null}
                {img1 ? <Thumb src={img1} /> : null}
              </div>
            </div>

            <p style={{ color: "#444" }}>{captionDescription}</p>

            <button
              id="btnWhatWeveDoneDetails"
              className="home-tab-details-btn"
              onClick={(e) => {
                e.stopPropagation();
                fetchImages();
                openHome("whatWeveDone");
              }}
            >
              See Details
            </button>
          </div>
        </div>
      </div>

      {/* Modal: What We Do — with dropdown */}
      {openHomeModal === "whatWeDo" && (
        <div
          className="home-modal active"
          role="dialog"
          aria-modal="true"
          onClick={closeHome}
        >
          <div
            className="home-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="home-modal-close"
              aria-label="Close"
              onClick={closeHome}
            >
              &times;
            </button>

            <h2 className="mb-2">What We Do</h2>
            <p className="text-muted" style={{ marginBottom: "1rem" }}>
              Choose a service category to explore what we offer.
            </p>

            {/* Dropdown */}
            <div style={{ maxWidth: 280 }}>
              <label
                htmlFor="whatWeDoSelect"
                className="form-label"
                style={{ fontWeight: 600 }}
              >
                Category
              </label>
              <select
                id="whatWeDoSelect"
                className="form-select"
                value={whatWeDoChoice}
                onChange={(e) => setWhatWeDoChoice(e.target.value)}
              >
                <option>Painting</option>
                <option>Tiling</option>
              </select>
            </div>

            {/* Dynamic list */}
            {renderServicesList()}
          </div>
        </div>
      )}

      {/* Modal: What We've Done — 2×2 grid (all four are images now) */}
      {openHomeModal === "whatWeveDone" && (
        <div
          className="home-modal active"
          role="dialog"
          aria-modal="true"
          onClick={closeHome}
        >
          <div
            className="home-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="home-modal-close"
              aria-label="Close"
              onClick={closeHome}
            >
              &times;
            </button>
            <h2>Gallery: Our Works</h2>

            {isLoadingImages && (
              <p style={{ marginTop: "1em" }}>Loading images…</p>
            )}
            {!isLoadingImages && imagesError && (
              <p style={{ marginTop: "1em", color: "#b00" }}>{imagesError}</p>
            )}

            {!isLoadingImages && !imagesError && (
              <>
                <div
                  style={{
                    marginTop: "12px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "160px 160px",
                    gap: "14px",
                  }}
                >
                  {/* Top-left → img0 */}
                  <div>
                    {img0 ? (
                      <GridImg
                        src={img0}
                        onClick={() => openLightbox(galleryImages, 0)}
                      />
                    ) : (
                      <PlaceholderBox />
                    )}
                  </div>

                  {/* Top-right → img1 */}
                  <div>
                    {img1 ? (
                      <GridImg
                        src={img1}
                        onClick={() => openLightbox(galleryImages, 1)}
                      />
                    ) : (
                      <PlaceholderBox />
                    )}
                  </div>

                  {/* Bottom-left → img2 (FIXED: was caption card) */}
                  <div>
                    {img2 ? (
                      <GridImg
                        src={img2}
                        onClick={() => openLightbox(galleryImages, 2)}
                      />
                    ) : (
                      <PlaceholderBox />
                    )}
                  </div>

                  {/* Bottom-right → img3 (falls back to placeholder) */}
                  <div>
                    {img3 ? (
                      <GridImg
                        src={img3}
                        onClick={() => openLightbox(galleryImages, 3)}
                      />
                    ) : (
                      <PlaceholderBox />
                    )}
                  </div>
                </div>

                <p style={{ marginTop: "1em" }}>
                  Explore more in our{" "}
                  <a href="#gallery" onClick={goToGallery}>
                    Gallery
                  </a>{" "}
                  section.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Lightbox */}
      <LightboxModal
        isOpen={lightboxOpen}
        images={lightboxImages}
        initialIndex={lightboxInitial}
        onClose={closeLightbox}
      />
    </section>
  );
}

/* Small grey block for empty cells */
const PlaceholderBox = () => (
  <div
    style={{
      width: "100%",
      height: "100%",
      borderRadius: "12px",
      background: "#eee",
      color: "#999",
      display: "grid",
      placeItems: "center",
      fontSize: "0.9rem",
      border: "1px solid #ddd",
    }}
  >
    No image
  </div>
);
